// One Ask AI conversation outside the widget's panel (the bar above the grid): the widget's own
// engine, the same way it uses it. The request is the widget's `{system, messages, tools, model}` to
// `/api/ai/chat`; the answer is read with the widget's SSE rules (its `readSSE`, which lives inside
// the AXAI-CHAT-SYNC block and cannot be imported); the system prompt, tools and tool calls are the
// page's `window.AXAI_*` hooks. Memory only: the caller keeps `history`, and nothing here touches
// the panel's.

/** A message as `/api/ai/chat` takes it (Anthropic Messages). */
export interface ChatMessage {
  role: "user" | "assistant";
  content: ContentBlock[];
}
type ContentBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: unknown }
  | { type: "tool_result"; tool_use_id: string; content: string; is_error?: boolean };

interface ToolCall {
  id: string;
  name: string;
  inputJson: string;
}

declare global {
  interface Window {
    AXAI_SYSTEM_PROMPT?: () => unknown;
    AXAI_HANDLE_TOOL_CALL?: (
      name: string,
      input: unknown,
      appendStatus: (text: string) => void,
      isStale: () => boolean,
    ) => Promise<string> | string | undefined;
    AXAI_ASK_EXAMPLES?: string[];
  }
}

/** The widget's safety cap: a looping tool call must not hang the page. */
const MAX_TOOL_ROUNDS = 10;
/** Turns kept for a follow-up: the chat sends them back each time, under the 64 KB body limit (P11). */
const KEEP_TURNS = 3;

/** The widget's `readSSE`: text deltas, tool calls (start + input_json_delta), and an error event. */
async function readSse(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let text = "";
  let error: string | null = null;
  const toolCalls: ToolCall[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6);
      if (data === "[DONE]") continue;
      try {
        const ev = JSON.parse(data);
        if (ev.type === "error") error = ev.error?.message || "Unknown error";
        if (ev.type === "content_block_delta" && ev.delta?.type === "text_delta") text += ev.delta.text;
        if (ev.type === "content_block_start" && ev.content_block?.type === "tool_use")
          toolCalls.push({ id: ev.content_block.id, name: ev.content_block.name, inputJson: "" });
        if (ev.type === "content_block_delta" && ev.delta?.type === "input_json_delta" && toolCalls.length)
          toolCalls[toolCalls.length - 1]!.inputJson += ev.delta.partial_json;
      } catch {
        // A partial or foreign line: skipped, as the widget does.
      }
    }
  }
  return { text, toolCalls, error };
}

/** The widget's default `chatModel` (its block's literal): the panel sends it when nothing is saved. */
const WIDGET_DEFAULT_MODEL = "us.anthropic.claude-sonnet-4-5-20250929-v1:0";

/** The model the panel sends: its picker's saved choice, else its default. The server checks its allowlist. */
function chatModel(): string {
  try {
    return localStorage.getItem("axaiChatModel") || WIDGET_DEFAULT_MODEL;
  } catch {
    return WIDGET_DEFAULT_MODEL;
  }
}

/** The last `KEEP_TURNS` turns, cut where a turn starts (a user message of text only), as the widget trims. */
export function trimHistory(history: ChatMessage[]): ChatMessage[] {
  const starts = history.flatMap((m, i) => (m.role === "user" && m.content.every((b) => b.type === "text") ? [i] : []));
  return starts.length > KEEP_TURNS ? history.slice(starts[starts.length - KEEP_TURNS]) : history;
}

export interface TurnResult {
  text: string;
  history: ChatMessage[];
}

/**
 * Asks `question` after `history`, running the page's tools until the model answers in text. A
 * stopped turn (`signal`) rejects with an AbortError and reports itself stale to every tool call,
 * so `apply_view` never sets the grid after Stop.
 */
export async function askTurn(
  question: string,
  history: ChatMessage[],
  opts: { signal: AbortSignal; onStatus?: (text: string) => void; extraSystem?: string },
): Promise<TurnResult> {
  const base = window.AXAI_SYSTEM_PROMPT?.();
  const system = [
    ...(Array.isArray(base) ? base : []),
    ...(opts.extraSystem ? [{ type: "text", text: opts.extraSystem }] : []),
  ];
  const hist: ChatMessage[] = [...history, { role: "user", content: [{ type: "text", text: question }] }];
  const stale = () => opts.signal.aborted;
  for (let round = 1; ; round++) {
    if (round > MAX_TOOL_ROUNDS)
      throw new Error("This is taking too many steps to answer — try rephrasing or narrowing the question.");
    const res = await fetch("/api/ai/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        system,
        messages: hist,
        tools: window.AXAI_TOOLS ?? [],
        model: chatModel(),
      }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(e.error || `HTTP ${res.status}`);
    }
    const out = await readSse(res.body);
    if (out.error) throw new Error(out.error);
    if (!out.toolCalls.length) {
      hist.push({ role: "assistant", content: [{ type: "text", text: out.text }] });
      return { text: out.text || "No response.", history: trimHistory(hist) };
    }
    const content: ContentBlock[] = out.text ? [{ type: "text", text: out.text }] : [];
    const results = await Promise.all(
      out.toolCalls.map(async (tc): Promise<ContentBlock> => {
        let input: unknown = {};
        try {
          input = JSON.parse(tc.inputJson || "{}");
        } catch {
          // As the widget: a malformed input is an empty one.
        }
        content.push({ type: "tool_use", id: tc.id, name: tc.name, input });
        const handle = window.AXAI_HANDLE_TOOL_CALL;
        if (!handle) return { type: "tool_result", tool_use_id: tc.id, is_error: true, content: "Unknown tool" };
        try {
          const result = await handle(tc.name, input, opts.onStatus ?? (() => {}), stale);
          return { type: "tool_result", tool_use_id: tc.id, content: String(result) };
        } catch (e) {
          return {
            type: "tool_result",
            tool_use_id: tc.id,
            is_error: true,
            content: `${tc.name} failed: ${e instanceof Error ? e.message : String(e)}`,
          };
        }
      }),
    );
    if (stale()) throw new DOMException("Stopped", "AbortError");
    hist.push({ role: "assistant", content }, { role: "user", content: results });
  }
}
